import "twin.macro";

import { Link } from "react-router";

import { Button, Fade } from "@mui/material";

import _ from "lodash";

import { OpaqueCard } from "@components/StyledComponents";
import useOrderableOrderWindowsQuery from "@features/orderWindows/useOrderableOrderWindowsQuery";
import { Type } from "@features/ui";
import { OrderWindow } from "@models/OrderWindow";
import { CldImage } from "@services/cloudinary";
import { formatUtcDate } from "@utility/utilityFunctions";

const SingleProgram = ({ orderWindow }: { orderWindow: OrderWindow }) => {
  const [program] = orderWindow.programs;
  return (
    <div tw="flex gap-6 pr-2 flex-none w-full">
      {program.cloudinaryId && (
        <CldImage
          tw="rounded-lg w-40 h-40 object-cover object-center shadow-sm"
          publicId={program.cloudinaryId}
          alt={""}
          size="medium"
        />
      )}
      <div tw="max-w-sm flex flex-col justify-between items-start pb-1">
        <div tw="line-clamp-4">
          <Type.H1>{program.name}</Type.H1>
          <Type.P>{program.description}</Type.P>
        </div>

        <div tw="flex gap-4 items-center mt-2">
          <Button
            variant="contained"
            tw="whitespace-nowrap shrink-0"
            component={Link}
            to={`/programs/${program.id}/order/${orderWindow.id}`}
          >
            View Program
          </Button>
          <Type.Small>On {orderWindow.name}</Type.Small>
        </div>
      </div>
    </div>
  );
};

const MultiProgram = ({ orderWindow }: { orderWindow: OrderWindow }) => {
  const maxFiveCldImages = _(orderWindow.programs)
    .orderBy([(p) => +p.id], ["desc"])
    .map((program) => program.cloudinaryId)
    .sortBy((id) => !id)
    .take(5)
    .reverse()
    .value();

  return (
    <div tw="flex gap-6 pr-2 flex-none w-full">
      <div tw="flex flex-row-reverse">
        {maxFiveCldImages.map((image, i) => {
          const ith = maxFiveCldImages.length - i - 1;
          return (
            <div
              tw="w-40 h-40 rounded-lg border-4 border-white bg-white -ml-28 last-of-type:ml-0! overflow-hidden relative last-of-type:shadow-md shadow-sm"
              key={`${image}-${i}`}
            >
              {image ? (
                <CldImage
                  tw="object-cover object-center w-full h-full"
                  style={{
                    filter: `brightness(${1 - ith * 0.06})`,
                    opacity: 1 - ith * 0.06,
                  }}
                  size="medium"
                  draggable={false}
                  publicId={image}
                  alt={""}
                />
              ) : (
                <div
                  tw="h-full bg-neutral-200"
                  style={{ filter: `brightness(${1 - ith * 0.02})` }}
                />
              )}
            </div>
          );
        })}
      </div>
      <div tw="max-w-sm flex flex-col justify-between items-start pb-1">
        <div tw="line-clamp-4">
          <Type.H1>{orderWindow.name}</Type.H1>
          <Type.P>{orderWindow.programs.length} programs</Type.P>
        </div>
        <Button
          variant="contained"
          tw="mt-2"
          component={Link}
          to={`/pre-orders/${orderWindow.id}`}
        >
          View Pre-Order
        </Button>
      </div>
    </div>
  );
};

const OrderWindowCard = ({
  orderWindow,
  index = 0,
}: {
  orderWindow: OrderWindow;
  index: number;
}) => {
  return (
    <Fade in timeout={500 + index * 800}>
      <OpaqueCard tw="min-w-max">
        {orderWindow.programs.length === 1 ? (
          <SingleProgram orderWindow={orderWindow} />
        ) : (
          <MultiProgram orderWindow={orderWindow} />
        )}
        <Type.P tw="mt-3">
          Ordering closes{" "}
          <b tw="font-medium text-primary-600">
            {formatUtcDate(orderWindow.closeDate, "PP")}
          </b>
          {" - "}
          In Market{" "}
          <b tw="font-medium text-primary-600">
            {formatUtcDate(orderWindow.inMarketDate, "PP")}
          </b>
        </Type.P>
      </OpaqueCard>
    </Fade>
  );
};

const PreOrderSection = () => {
  const { data: orderWindows } = useOrderableOrderWindowsQuery();
  if (orderWindows.length === 0) return null;
  return (
    <div>
      <Type.H1 tw="mb-2">Pre-Orders</Type.H1>
      <div tw="flex gap-4 overflow-x-auto  pb-2 -mr-7 pr-7">
        {orderWindows?.map((orderWindow, index) => (
          <OrderWindowCard
            orderWindow={orderWindow}
            key={orderWindow.id}
            index={index}
          />
        ))}
      </div>
    </div>
  );
};

export default PreOrderSection;
